import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import xpService from './xpService';
import priceService from './priceService';
import logger from '../utils/logger';

class CrowdSourcedChecker {
  constructor() {
    this.isChecking = false;
    this.lastCheckTime = 0;
    this.checkInterval = null;
    this.apiCallsThisHour = 0;
    this.hourResetTime = Date.now() + 3600000; // 1 hour from now
    this.maxCallsPerHour = 50; // Conservative limit
  }

  // Initialize the checker when app starts
  init() {
    logger.log('🚀 Initializing crowd-sourced prediction checker...');
    
    // Check just 2 predictions on load to save API calls
    this.checkRandomPredictions(2);
    
    // Set up periodic checking every 5 minutes (less frequent)
    this.checkInterval = setInterval(() => {
      this.checkRandomPredictions(2); // Only check 2 at a time
    }, 5 * 60 * 1000); // 5 minutes
  }

  // Stop the checker
  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  // Check a batch of random active predictions
  async checkRandomPredictions(batchSize = 5) {
    if (this.isChecking) {
      logger.log('⏳ Already checking predictions, skipping...');
      return;
    }

    // Reset hour counter if needed
    const now = Date.now();
    if (now > this.hourResetTime) {
      this.apiCallsThisHour = 0;
      this.hourResetTime = now + 3600000;
    }

    // Check if we've hit our API limit
    if (this.apiCallsThisHour >= this.maxCallsPerHour) {
      logger.log('⚠️ API rate limit reached for this hour, skipping check...');
      return;
    }

    try {
      this.isChecking = true;
      
      // Don't check too frequently
      if (now - this.lastCheckTime < 30000) { // 30 seconds
        logger.log('⏱️ Too soon since last check, skipping...');
        return;
      }

      logger.log(`🔍 Crowd-sourced check: Looking for ${batchSize} predictions to check...`);

      // Query for active predictions that haven't been checked recently
      // Prioritize predictions that are close to expiring
      const predictionsRef = collection(db, 'priceCalls');
      const q = query(
        predictionsRef,
        where('status', '==', 'active'),
        where('isResolved', '==', false),
        orderBy('expiresAt', 'asc'), // Check ones expiring soon first
        limit(batchSize * 2) // Get extra in case some were recently checked
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        logger.log('✨ No active predictions to check');
        return;
      }

      let checkedCount = 0;
      const predictions = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const prediction = { id: doc.id, ...data };
        
        // Skip if checked in the last 5 minutes
        if (data.lastCheckedAt && (now - data.lastCheckedAt.toMillis()) < 5 * 60 * 1000) {
          logger.log(`⏭️ Skipping ${prediction.tokenSymbol} - recently checked`);
          return;
        }
        
        predictions.push(prediction);
      });

      // Shuffle to randomize which ones we check (fairness)
      const shuffled = predictions.sort(() => Math.random() - 0.5);
      const toCheck = shuffled.slice(0, batchSize);

      logger.log(`📊 Checking ${toCheck.length} predictions...`);

      // Check each prediction
      for (const prediction of toCheck) {
        try {
          logger.log(`🎲 Checking ${prediction.tokenSymbol} (${prediction.multiplier}x) for user ${prediction.username}`);
          
          // Update last checked timestamp
          await updateDoc(doc(db, 'priceCalls', prediction.id), {
            lastCheckedAt: serverTimestamp()
          });

          // Check if this specific prediction should be resolved
          const currentPrice = await priceService.getDexScreenerPrice(prediction.tokenMint);
          if (currentPrice) {
            const actualMultiplier = currentPrice / prediction.currentPrice;
            
            // Check if target multiplier reached
            if (actualMultiplier >= prediction.multiplier) {
              await xpService.resolvePriceCall(prediction.id, prediction, 'won', currentPrice, actualMultiplier);
            }
          }
          
          // Count this API call
          this.apiCallsThisHour++;
          checkedCount++;
        } catch (error) {
          logger.error(`❌ Error checking prediction ${prediction.id}:`, error);
        }
      }

      logger.log(`✅ Crowd-sourced check complete: ${checkedCount} predictions checked`);
      this.lastCheckTime = now;

    } catch (error) {
      logger.error('❌ Error in crowd-sourced checker:', error);
    } finally {
      this.isChecking = false;
    }
  }

  // Check specific user's predictions (when they view their profile)
  async checkUserPredictions(userId, maxResults = 10) {
    try {
      logger.log(`👤 Checking predictions for user ${userId}...`);
      
      const predictionsRef = collection(db, 'priceCalls');
      const q = query(
        predictionsRef,
        where('userId', '==', userId),
        where('status', '==', 'active'),
        where('isResolved', '==', false),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      let checkedCount = 0;

      for (const doc of snapshot.docs) {
        const prediction = { id: doc.id, ...doc.data() };
        try {
          // Check if this specific prediction should be resolved
          const currentPrice = await priceService.getDexScreenerPrice(prediction.tokenMint);
          if (currentPrice) {
            const actualMultiplier = currentPrice / prediction.currentPrice;
            
            // Check if target multiplier reached
            if (actualMultiplier >= prediction.multiplier) {
              await xpService.resolvePriceCall(prediction.id, prediction, 'won', currentPrice, actualMultiplier);
            }
          }
          checkedCount++;
        } catch (error) {
          logger.error(`Error checking user prediction ${prediction.id}:`, error);
        }
      }

      logger.log(`✅ Checked ${checkedCount} predictions for user`);
      return checkedCount;

    } catch (error) {
      logger.error('Error checking user predictions:', error);
      return 0;
    }
  }
}

export default new CrowdSourcedChecker();